import { Vertical } from "@/components/standard/BFLayout"
import { Linking, Pressable, ScrollView } from "react-native"
import { Body, H2, H5, Tiny } from "@/components/standard/BFText"

import {
  AltitudeProductCard,
  ReFiProductCard,
  StandardProductCard,
  ZeroGravityProductCard,
} from "@/screens/prequal/components/LoanProductCard"

import { useUserQualifiedForContactLink } from "@/hooks/useUserQualifiedForContactLink"
import { router } from "expo-router"
import { loanProductsQuestionClicked } from "@/analytics/events/loans"
import { useCallback, useEffect, useRef } from "react"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from "@gorhom/bottom-sheet"
import { BFColors } from "@/constants/Colors"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { useSafeAreaInsets } from "react-native-safe-area-context"

export const ProductPopup = ({
  visible,
  onClose,
  includeActions = true,
  title = "No property, yet?",
  description = "We need an address to provide a quote, but here are loan options that may be available once you’re ready:",
}: {
  visible: boolean
  onClose: (moveOn: boolean) => void
  includeActions?: boolean
  title?: string
  description?: string
}) => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const qualifiedForContactLink = useUserQualifiedForContactLink()
  const insets = useSafeAreaInsets()

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        onPress={() => {
          onClose(false)
        }}
      />
    ),
    []
  )

  useEffect(() => {
    if (!visible) {
      bottomSheetModalRef.current?.dismiss()
      onClose(false)
    } else {
      bottomSheetModalRef.current?.present()
    }
  }, [visible])

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      backdropComponent={renderBackdrop}
      backgroundStyle={{ backgroundColor: "#FFFFFF" }}
      handleIndicatorStyle={{ backgroundColor: BFColors.offWhite }}
      enableDynamicSizing={true}
    >
      <BottomSheetView>
        <Vertical
          className={"justify-between flex-1 px-6"}
          style={{ paddingBottom: insets.bottom + 20 }}
        >
          <Vertical className={"gap-2 mt-10"}>
            <Vertical className={"gap-2"}>
              <H2 className={"text-center"} color={"primary"}>
                {title}
              </H2>
              <Body className={"text-center"} color={"primary"}>
                {description}
              </Body>
            </Vertical>
            <ScrollView
              horizontal={true}
              contentContainerClassName={"gap-5 flex-row"}
              className={"max-h-[220px]"}
            >
              <StandardProductCard />
              <AltitudeProductCard />
              <ZeroGravityProductCard />
              <ReFiProductCard />
            </ScrollView>
          </Vertical>
          <Tiny color={"primary"} className={"mt-3"}>
            *Eligibility is based on self-reported information provided. Final
            verification of qualifications will be required.
          </Tiny>
          {includeActions && (
            <Vertical className={"my-5 gap-5"}>
              <PrimaryButton
                title={"Return Home"}
                testID={"home"}
                onPress={() => {
                  bottomSheetModalRef.current?.dismiss()
                  onClose(false)
                  router.push("/")
                }}
              />
              {qualifiedForContactLink && (
                <Pressable
                  testID={"contact-us"}
                  onPress={() => {
                    loanProductsQuestionClicked("Loan Property Search")
                    void Linking.openURL(
                      "https://backflip.app.link/e/WBWTyZcnSLb"
                    )
                  }}
                >
                  <H5
                    className={"underline text-center"}
                    color={"primaryLight"}
                  >
                    Speak with a lending expert
                  </H5>
                </Pressable>
              )}
            </Vertical>
          )}
        </Vertical>
      </BottomSheetView>
    </BottomSheetModal>
  )
}
